import React, { useEffect, useState, useMemo } from 'react';
import PageLayout from 'components/PageLayout';
import LoadingState from 'components/LoadingState';
import ErrorState from 'components/ErrorState';
import {
  EmptyState,
  JudgmentStatistics,
  JudgmentFilters,
  ActionLogPanel,
} from 'components/Judgment';
import QualificationSubmissionsTable from 'components/Judgment/QualificationSubmissionsTable';
import { useQualificationJudgmentData } from 'hooks/useQualificationJudgmentData';
import { useAuth } from 'contexts/AuthContext';
import AccessDenied from 'components/AccessDenied';
import NotAuthenticated from 'components/NotAuthenticated';
import { trackEvent } from 'services/firebase';
import { exportQualificationJudgmentToExcel } from 'services/excelExportService';
import type { StageActionLog } from 'components/Judgment/SubmissionRow';

const QualificationRankingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const {
    submissions,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filterCategory,
    setFilterCategory,
    year,
    setYear,
    sortField,
    sortDirection,
    expandedRows,
    handleRefresh,
    toggleRowExpansion,
    handleSort,
    categories,
    filteredAndSortedSubmissions,
    stats,
    rubrics,
    getMainDescription,
    updateSubmissionStageLocal,
    // Access control
    userHasAccess,
    userPillar,
    userIsSuperAdmin,
    userIsPillarPIC,
  } = useQualificationJudgmentData();

  const actionLogs = useMemo(() => {
    const logs: StageActionLog[] = [];
    submissions.forEach(s => {
      if (s.stage_action_logs && Array.isArray(s.stage_action_logs)) {
        logs.push(...s.stage_action_logs);
      }
    });
    // Sort oldest to newest (ascending) so the Panel can reverse it to display newest first
    return logs.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [submissions]);

  useEffect(() => {
    trackEvent('page_view', {
      page_title: 'Qualification Ranking',
      page_path: '/submissions',
    });
  }, []);

  // Handle Excel export
  const handleExportToExcel = async () => {
    if (
      !filteredAndSortedSubmissions ||
      filteredAndSortedSubmissions.length === 0
    ) {
      alert('No data to export');
      return;
    }

    setIsExporting(true);

    try {
      const filename = await exportQualificationJudgmentToExcel(
        filteredAndSortedSubmissions,
        `qualification-judgment-${filterCategory !== 'all' ? filterCategory.toLowerCase().replace(/\s+/g, '-') : 'all-categories'}`
      );

      // Track export event
      trackEvent('qualification_judgment_export', {
        total_submissions: filteredAndSortedSubmissions.length,
        filter_category: filterCategory,
        filename: filename,
      });
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // If not authenticated, show not authenticated message
  if (!isAuthenticated()) {
    return (
      <PageLayout
        title="Qualification Ranking"
        subtitle="Authentication required to access qualification ranking data."
      >
        <NotAuthenticated
          message="Please log in to access the qualification ranking page."
          buttonLink="/"
          buttonClassName="bg-red-smdd text-white hover:bg-red-smdd-dark"
          iconBgClassName="bg-yellow-100"
          iconClassName="text-yellow-600"
          className="text-center py-12"
          buttonText="Go to Home"
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Qualification Ranking"
      subtitle={
        userIsSuperAdmin
          ? 'Review and evaluate qualification submissions from ALL pillars with super admin access and detailed jury scoring insights.'
          : userHasAccess && userPillar
            ? `Review and evaluate qualification submissions for ${userPillar} pillar with detailed jury scoring insights.`
            : 'Review and evaluate qualification submissions with detailed jury scoring insights and comprehensive evaluation criteria.'
      }
    >
      {loading ? (
        <LoadingState message="Loading qualification submissions for judgment review..." />
      ) : error ? (
        <ErrorState
          title="Error Loading Qualification Submissions"
          error={error}
          onRetry={handleRefresh}
        />
      ) : !userHasAccess ? (
        <AccessDenied
          message="You don't have permission to view qualification ranking data. Access is restricted to authorized personnel with super admin or pillar PIC privileges."
          iconBgClassName="bg-red-100"
          iconClassName="text-red-600"
          className="text-center py-12"
          buttonText="Return to Home"
        />
      ) : (
        <>
          <JudgmentStatistics stats={stats} />
          <JudgmentFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
            categories={categories}
            handleRefresh={handleRefresh}
            loading={loading}
            userPillar={userPillar}
            userIsSuperAdmin={userIsSuperAdmin}
            year={year}
            setYear={setYear}
          />
          {filteredAndSortedSubmissions.length === 0 ? (
            <EmptyState
              searchQuery={searchQuery}
              filterCategory={filterCategory}
            />
          ) : (
            <>
              <QualificationSubmissionsTable
                submissions={filteredAndSortedSubmissions}
                expandedRows={expandedRows}
                toggleRowExpansion={toggleRowExpansion}
                sortField={sortField}
                sortDirection={sortDirection}
                handleSort={handleSort}
                getMainDescription={getMainDescription}
                rubrics={rubrics}
                showStageAction={userIsSuperAdmin || userIsPillarPIC}
                showActionMaterial={userIsSuperAdmin}
                onStageChange={updateSubmissionStageLocal}
              />

              {/* Export Button - Bottom Right */}
              <div className="flex justify-end mt-8 mb-6">
                <button
                  onClick={handleExportToExcel}
                  disabled={
                    isExporting || filteredAndSortedSubmissions.length === 0
                  }
                  className={`inline-flex items-center gap-3 px-8 py-3 rounded-full font-baijamjuree font-semibold text-sm uppercase tracking-wide transition-all duration-200 shadow-lg border-2 ${
                    isExporting || filteredAndSortedSubmissions.length === 0
                      ? 'bg-gray-400 text-gray-600 border-gray-300 cursor-not-allowed'
                      : 'bg-black text-white border-black hover:bg-gray-800 hover:border-gray-800 hover:shadow-xl transform hover:scale-105 active:scale-95'
                  }`}
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                        />
                      </svg>
                      Export to Excel
                    </>
                  )}
                </button>
              </div>

              {/* Action Log Panel */}
              <ActionLogPanel logs={actionLogs} />
            </>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default QualificationRankingPage;
